import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Title } from "react-admin";
import { deleteJson, getJson, postJson } from "../../utils/api";
import { Curriculum } from "../../models";

const ADMIN = "/admin";
const SLUG_RE = /[^a-z0-9-]/g;

export const CurriculaList: React.FC = () => {
  const navigate = useNavigate();
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newId, setNewId] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchCurricula = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getJson(`${ADMIN}/curricula`);
      const { data } = await response.json();
      setCurricula(data ?? []);
    } catch (e) {
      console.error("Failed to fetch curricula", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurricula();
  }, [fetchCurricula]);

  const handleCreate = async () => {
    if (!newId || !newTitle) return;
    setCreating(true);
    try {
      await postJson(`${ADMIN}/curricula`, {
        id: newId,
        title: newTitle,
        description: newDescription,
      });
      setCreateOpen(false);
      setNewId("");
      setNewTitle("");
      setNewDescription("");
      await fetchCurricula();
    } catch (e: any) {
      alert(e?.error?.detail ?? "Failed to create curriculum");
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Archive curriculum "${id}"? This will also archive all its units and lessons.`
      )
    ) {
      return;
    }
    try {
      await deleteJson(`${ADMIN}/curricula/${id}`);
      await fetchCurricula();
    } catch (err) {
      console.error("Failed to archive", err);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Curricula" />
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Curricula</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Units</TableCell>
              <TableCell align="center">Lessons</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : curricula.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No curricula found
                </TableCell>
              </TableRow>
            ) : (
              curricula.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/curricula/${c.id}`)}
                >
                  <TableCell>{c.id}</TableCell>
                  <TableCell>{c.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={c.is_active ? "Active" : "Inactive"}
                      color={c.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{c.unit_count}</TableCell>
                  <TableCell align="center">{c.total_lessons}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleArchive(c.id, e)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Curriculum</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="ID (slug)"
            fullWidth
            value={newId}
            onChange={(e) =>
              setNewId(e.target.value.toLowerCase().replace(SLUG_RE, ""))
            }
            helperText="Lowercase, hyphens only (e.g. credit-basics)"
          />
          <TextField
            margin="dense"
            label="Title"
            fullWidth
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating || !newId || !newTitle}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
